import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MONEY_DECIMALS } from '../../../../../utils/money';
import { ActionIcon, Button, Checkbox, FileInput, Modal, MultiSelect, NumberInput, SegmentedControl, Switch, TextInput, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button as SharedButton } from '../../../../../components/Button';
import { useProductReorderController } from './useProductReorderController';
import { ProductSettingsRow } from './ProductSettingsRow';
import { SortableProductRow } from './SortableProductRow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faXmark as faXmarkRemoveDef,
  faSort as faSortDef,
  faSortUp as faSortUpDef,
  faSortDown as faSortDownDef,
  faArrowsUpDown as faArrowsUpDownDef,
} from '@fortawesome/free-solid-svg-icons';
import { PrinterView } from '../../../../../services/printers/printers.service';
import { ProductView } from '../../../../../services/products/products.service';
import type { AreaPrintRule } from 'shared-models/browser';
import type { CategoryReportGroup, CategoryView } from '../../../../../services/categories/categories.service';
import { compareCategoryNamesForDisplay } from '../../../../../services/categories/category-order.util';
import type { ProductFormRow, ProductFormState } from '../../../core/pos-shell-page.types';
import { InlineCategoryCreateField } from '../inline-category-create-field/InlineCategoryCreateField';
import { BulkEditProductsModal } from '../bulk-edit-products-modal/BulkEditProductsModal';
import { ProductDescriptionModal } from '../product-description-modal/ProductDescriptionModal';
import { useProductBulkEdit, type BulkUpdateProductsPayload } from '../bulk-edit-products-modal/useProductBulkEdit';
import { useProductSettingsListController } from './useProductSettingsListController';
import type { KdsFilter, ProductSortKey, StatusFilter } from './product-filter-sort.utils';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from '../shared/settings-shared.module.scss';
import bulkClasses from '../shared/product-settings.module.scss';

const faXmarkRemove = faXmarkRemoveDef as IconProp;
const faSort = faSortDef as IconProp;
const faSortUp = faSortUpDef as IconProp;
const faSortDown = faSortDownDef as IconProp;
const faArrowsUpDown = faArrowsUpDownDef as IconProp;

const PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY = 'pos.product-settings.column-widths.v1';
const ROW_ESTIMATED_HEIGHT = 53;
const VIRTUALIZE_THRESHOLD = 24;
const PRODUCT_COLUMN_COUNT = 10;

type ProductResizableColumnKey =
  | 'name'
  | 'price'
  | 'category'
  | 'image'
  | 'status'
  | 'printers'
  | 'kds'
  | 'actions';

type ProductRoutingChoice = 'kds' | 'printer' | 'both';

const DEFAULT_COLUMN_WIDTHS: Record<ProductResizableColumnKey, number> = {
  name: 220,
  price: 92,
  category: 170,
  image: 72,
  status: 100,
  printers: 240,
  kds: 72,
  actions: 180,
};

const MIN_COLUMN_WIDTHS: Record<ProductResizableColumnKey, number> = {
  name: 140,
  price: 72,
  category: 120,
  image: 56,
  status: 84,
  printers: 140,
  kds: 56,
  actions: 152,
};

type ProductSettingsSectionProps = {
  productFormRows: ProductFormRow[];
  activePrinters: PrinterView[];
  settingsProducts: ProductView[];
  categories: CategoryView[];
  employeeSession: string | null;
  isSavingProduct: boolean;
  editingProductId: number | null;
  isLoadingProducts: boolean;
  isLoadingPrinters: boolean;
  isTogglingProductStatus: boolean;
  onChangeProductFormRowField: (index: number, field: keyof ProductFormState, value: string | number | number[] | boolean | null) => void;
  onProductFormRowImageChange: (index: number, file: File | null) => void;
  onAddProductFormRow: () => void;
  onRemoveProductFormRow: (index: number) => void;
  onSaveProduct: (event: React.FormEvent<HTMLFormElement>) => void;
  onCancelEdit: () => void;
  onRefreshProducts: () => void;
  onExportProducts: () => void;
  onOpenImport: () => void;
  onEditProduct: (product: ProductView) => void;
  onOpenOptions: (product: ProductView) => void;
  areaPrintRules?: AreaPrintRule[];
  variantComboCountByProductId?: Record<number, number>;
  onDeactivateProduct: (productId: number) => void;
  onActivateProduct: (productId: number) => void;
  onToggleProductKds: (productId: number, nextKds: boolean) => void;
  isProductKdsUpdating: (productId: number) => boolean;
  onCreateCategory: (name: string, reportGroup: CategoryReportGroup | null) => Promise<number | null>;
  onBulkUpdateProducts: (payload: BulkUpdateProductsPayload) => Promise<void>;
};

const ProductSettingsSectionComponent: React.FC<ProductSettingsSectionProps> = ({
  productFormRows,
  activePrinters,
  settingsProducts,
  categories,
  employeeSession,
  isSavingProduct,
  editingProductId,
  isLoadingProducts,
  isLoadingPrinters,
  isTogglingProductStatus,
  onChangeProductFormRowField,
  onProductFormRowImageChange,
  onAddProductFormRow,
  onRemoveProductFormRow,
  onSaveProduct,
  onCancelEdit,
  onRefreshProducts,
  onExportProducts,
  onOpenImport,
  onEditProduct,
  onOpenOptions,
  areaPrintRules = [],
  variantComboCountByProductId = {},
  onDeactivateProduct,
  onActivateProduct,
  onToggleProductKds,
  isProductKdsUpdating,
  onCreateCategory,
  onBulkUpdateProducts,
}) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [descriptionProductId, setDescriptionProductId] = useState<number | null>(null);
  const descriptionProduct = settingsProducts.find((product) => product.id === descriptionProductId) ?? null;
  const isModalOpen = isCreateModalOpen || editingProductId !== null;
  const isEditMode = editingProductId !== null;
  const editingProduct = isEditMode
    ? settingsProducts.find((product) => product.id === editingProductId) ?? null
    : null;

  const printerOptions = useMemo(
    () => activePrinters.map((printer) => ({ value: String(printer.id), label: printer.name })),
    [activePrinters],
  );
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => category.isActive)
        .sort((a, b) => compareCategoryNamesForDisplay(a.name, b.name))
        .map((category) => ({ value: String(category.id), label: category.name })),
    [categories],
  );
  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );
  const areaPrintRuleOptions = useMemo(
    () => areaPrintRules.map((rule) => ({ id: rule.id, name: rule.name })),
    [areaPrintRules],
  );
  const printerNameById = useMemo(
    () => new Map(activePrinters.map((printer) => [printer.id, printer.name])),
    [activePrinters],
  );

  // New assignments offer active printers only; a persisted assignment to a now-disabled printer is
  // kept (with a מושבתת badge) so editing the field never silently drops it.
  const printerOptionsForSelected = useCallback(
    (selectedIds: number[]) => {
      const known = new Set(printerOptions.map((option) => option.value));
      const extras = selectedIds
        .filter((id) => !known.has(String(id)))
        .map((id) => ({ value: String(id), label: `${printerNameById.get(id) ?? `מדפסת #${id}`} (מושבתת)` }));
      return extras.length > 0 ? [...printerOptions, ...extras] : printerOptions;
    },
    [printerOptions, printerNameById],
  );

  const list = useProductSettingsListController({ products: settingsProducts, categoryNameById, printerNameById });
  const { visibleProducts, sortKey, sortDir } = list;

  // Reorder is an explicit mode; normal mode mounts zero dnd wiring.
  const [reorderMode, setReorderMode] = useState(false);
  // Reorder mode (or no active column sort) shows the persisted display order.
  const showDisplayOrder = reorderMode || sortKey === null;
  const filteredVisibleIds = useMemo(() => visibleProducts.map((product) => product.id), [visibleProducts]);
  const reorder = useProductReorderController({ products: settingsProducts, visibleIds: filteredVisibleIds, employeeSession, enabled: reorderMode });
  const displayProducts = showDisplayOrder ? reorder.orderedVisible : visibleProducts;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Windowing runs in normal mode on every viewport; reorder mode renders the full list for drag.
  const isCompact = useMediaQuery('(max-width: 768px)', false);
  const isTabletViewport = useMediaQuery('(max-width: 1200px)', false);
  const isProductEditorTabletFullscreen = isTabletViewport;
  const productEditorModalSize = isProductEditorTabletFullscreen ? '100vw' : (isCompact ? '98vw' : 'lg');
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  // Defer mounting the (windowed) row body one paint so the modal shell/toolbar opens instantly.
  const [bodyMounted, setBodyMounted] = useState(false);
  useEffect(() => {
    setBodyMounted(true);
  }, []);

  const bulk = useProductBulkEdit({ onBulkUpdate: onBulkUpdateProducts });

  const handleToggleReorderMode = () => {
    setReorderMode((previous) => {
      const next = !previous;
      if (next) {
        list.clearSort();
        bulk.clearSelection();
      }
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    void reorder.handleDragEnd(Number(active.id), Number(over.id));
  };

  const visibleIds = useMemo(() => displayProducts.map((product) => product.id), [displayProducts]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => bulk.isSelected(id));
  const someSelected = bulk.selectedCount > 0 && !allSelected;

  // Windowing is desktop/tablet-table only; mobile card mode renders the full (bounded) list —
  // card-mode windowing produced an empty viewport, so we fall back to unwindowed cards there.
  const shouldVirtualize = bodyMounted && !reorderMode && !isCompact && displayProducts.length > VIRTUALIZE_THRESHOLD;
  const rowVirtualizer = useVirtualizer({
    count: displayProducts.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => ROW_ESTIMATED_HEIGHT,
    // Rows carry a bottom margin outside their border box, so measure it in or the total undercounts.
    measureElement: (element: HTMLElement) =>
      element.offsetHeight + (parseFloat(getComputedStyle(element).marginBottom) || 0),
    overscan: 16,
    getItemKey: (index) => displayProducts[index]?.id ?? index,
    enabled: shouldVirtualize,
  });
  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : [];
  const virtualPaddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom =
    virtualItems.length > 0 ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;

  // Latest-ref bundle keeps the row callbacks below referentially stable (memo boundary).
  const rowHandlersRef = useRef({
    onEditProduct,
    onOpenOptions,
    onDeactivateProduct,
    onActivateProduct,
    onToggleProductKds,
    toggle: bulk.toggle,
  });
  rowHandlersRef.current = {
    onEditProduct,
    onOpenOptions,
    onDeactivateProduct,
    onActivateProduct,
    onToggleProductKds,
    toggle: bulk.toggle,
  };

  const activeColumnResizeRef = useRef<{
    key: ProductResizableColumnKey;
    startX: number;
    startWidth: number;
    directionFactor: 1 | -1;
  } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<ProductResizableColumnKey, number>>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_COLUMN_WIDTHS;
    }
    try {
      const raw = window.localStorage.getItem(PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY);
      if (!raw) {
        return DEFAULT_COLUMN_WIDTHS;
      }
      const parsed = JSON.parse(raw) as Partial<Record<ProductResizableColumnKey, number>>;
      const merged = { ...DEFAULT_COLUMN_WIDTHS };
      (Object.keys(DEFAULT_COLUMN_WIDTHS) as ProductResizableColumnKey[]).forEach((key) => {
        const next = parsed[key];
        if (typeof next === 'number' && Number.isFinite(next)) {
          merged[key] = Math.max(MIN_COLUMN_WIDTHS[key], Math.round(next));
        }
      });
      return merged;
    } catch {
      return DEFAULT_COLUMN_WIDTHS;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(PRODUCTS_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const activeResize = activeColumnResizeRef.current;
      if (!activeResize) {
        return;
      }
      const { key, startX, startWidth, directionFactor } = activeResize;
      const delta = event.clientX - startX;
      const next = Math.max(MIN_COLUMN_WIDTHS[key], Math.round(startWidth + (delta * directionFactor)));
      setColumnWidths((previous) => (previous[key] === next ? previous : { ...previous, [key]: next }));
    };

    const handleMouseUp = () => {
      activeColumnResizeRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const beginColumnResize = (key: ProductResizableColumnKey) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const tableDirection = (event.currentTarget.closest('table') as HTMLTableElement | null)?.dir;
    const direction =
      tableDirection
      || document.documentElement.getAttribute('dir')
      || document.body.getAttribute('dir')
      || window.getComputedStyle(event.currentTarget).direction;
    activeColumnResizeRef.current = {
      key,
      startX: event.clientX,
      startWidth: columnWidths[key],
      directionFactor: direction === 'rtl' ? -1 : 1,
    };
  };

  const handleRowEdit = useCallback((product: ProductView) => rowHandlersRef.current.onEditProduct(product), []);
  const handleRowOpenOptions = useCallback((product: ProductView) => rowHandlersRef.current.onOpenOptions(product), []);
  const handleRowEditDescription = useCallback((product: ProductView) => setDescriptionProductId(product.id), []);
  const handleRowDeactivate = useCallback((productId: number) => rowHandlersRef.current.onDeactivateProduct(productId), []);
  const handleRowActivate = useCallback((productId: number) => rowHandlersRef.current.onActivateProduct(productId), []);
  const handleRowToggleKds = useCallback(
    (productId: number, next: boolean) => rowHandlersRef.current.onToggleProductKds(productId, next),
    [],
  );
  const handleRowToggleSelect = useCallback((productId: number) => rowHandlersRef.current.toggle(productId), []);

  const rowDataProps = (product: ProductView) => ({
    categoryName: product.categoryId !== null ? categoryNameById.get(product.categoryId) ?? '—' : '—',
    printerNamesText:
      product.printerIds.length > 0
        ? product.printerIds.map((printerId) => printerNameById.get(printerId) ?? `#${printerId}`).join(', ')
        : '—',
    hasPrinters: product.printerIds.length > 0,
    variantComboCount: variantComboCountByProductId[product.id] ?? 0,
    isSelected: bulk.isSelected(product.id),
    isTogglingProductStatus,
    isKdsUpdating: isProductKdsUpdating(product.id),
    onEdit: handleRowEdit,
    onEditDescription: handleRowEditDescription,
    onOpenOptions: handleRowOpenOptions,
    onDeactivate: handleRowDeactivate,
    onActivate: handleRowActivate,
    onToggleKds: handleRowToggleKds,
    onToggleSelect: handleRowToggleSelect,
  });

  // Windowed rows are measured so estimate drift no longer exposes the spacer rows mid-scroll.
  const renderProductRow = (product: ProductView, virtualIndex?: number) => (
    <ProductSettingsRow
      key={product.id}
      product={product}
      reorderMode={false}
      cardTapSelect
      rowIndex={virtualIndex}
      rowRef={virtualIndex === undefined ? undefined : rowVirtualizer.measureElement}
      {...rowDataProps(product)}
    />
  );

  const renderSortableProductRow = (product: ProductView) => (
    <SortableProductRow key={product.id} product={product} {...rowDataProps(product)} />
  );

  const emptyStateRow =
    displayProducts.length === 0 && !isLoadingProducts ? (
      <tr>
        <td colSpan={PRODUCT_COLUMN_COUNT} className={classes.empty}>
          {settingsProducts.length === 0 ? 'אין מוצרים לטננט זה עדיין.' : 'לא נמצאו מוצרים תואמים לחיפוש.'}
        </td>
      </tr>
    ) : null;

  const renderProductTable = (reorder: boolean) => (
    <table className={`${classes.table} ${bulkClasses.productsTable}`}>
      <colgroup>
        <col style={{ width: 36 }} />
        <col style={{ width: 40 }} />
        <col data-testid="product-settings-col-name" style={{ width: columnWidths.name }} />
        <col data-testid="product-settings-col-price" style={{ width: columnWidths.price }} />
        <col data-testid="product-settings-col-category" style={{ width: columnWidths.category }} />
        <col data-testid="product-settings-col-image" style={{ width: columnWidths.image }} />
        <col data-testid="product-settings-col-status" style={{ width: columnWidths.status }} />
        <col data-testid="product-settings-col-printers" style={{ width: columnWidths.printers }} />
        <col data-testid="product-settings-col-kds" style={{ width: columnWidths.kds }} />
        <col data-testid="product-settings-col-actions" style={{ width: columnWidths.actions }} />
      </colgroup>
      <thead>
        <tr>
          <th className={bulkClasses.dragCell} aria-hidden />
          <th className={bulkClasses.selectCell}>
            {reorder ? null : (
              <Checkbox
                data-testid="product-settings-select-all"
                aria-label="בחר הכל (טבלה)"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(event) => bulk.setAll(visibleIds, event.currentTarget.checked)}
              />
            )}
          </th>
          {renderSortHeader('name', 'name', 'שם')}
          {renderSortHeader('price', 'price', 'מחיר')}
          {renderSortHeader('category', 'category', 'קטגוריה')}
          {renderPlainHeader('תמונה', 'image')}
          {renderSortHeader('status', 'status', 'סטטוס')}
          {renderSortHeader('printers', 'printers', 'מדפסות')}
          {renderSortHeader('kds', 'kds', 'KDS')}
          {renderPlainHeader('פעולות', 'actions')}
        </tr>
      </thead>
      {reorder ? (
        <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
          <tbody>
            {displayProducts.map((product) => renderSortableProductRow(product))}
            {emptyStateRow}
          </tbody>
        </SortableContext>
      ) : (
        <tbody>
          {!bodyMounted && displayProducts.length > 0 ? (
            <tr>
              <td colSpan={PRODUCT_COLUMN_COUNT} className={classes.empty}>טוען מוצרים...</td>
            </tr>
          ) : shouldVirtualize ? (
            <>
              {virtualPaddingTop > 0 && (
                <tr aria-hidden className={bulkClasses.virtualSpacerRow}>
                  <td colSpan={PRODUCT_COLUMN_COUNT} data-testid="product-settings-virtual-spacer" style={{ height: virtualPaddingTop, padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map((virtualItem) => renderProductRow(displayProducts[virtualItem.index], virtualItem.index))}
              {virtualPaddingBottom > 0 && (
                <tr aria-hidden className={bulkClasses.virtualSpacerRow}>
                  <td colSpan={PRODUCT_COLUMN_COUNT} data-testid="product-settings-virtual-spacer" style={{ height: virtualPaddingBottom, padding: 0, border: 0 }} />
                </tr>
              )}
            </>
          ) : (
            displayProducts.map((product) => renderProductRow(product))
          )}
          {bodyMounted && emptyStateRow}
        </tbody>
      )}
    </table>
  );

  const closeModal = () => {
    setCreateModalOpen(false);
    onCancelEdit();
  };

  const preventProductEditorSelection = (event: React.SyntheticEvent<HTMLFormElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const end = target.value.length;
    if (target.selectionStart !== end || target.selectionEnd !== end) {
      target.setSelectionRange(end, end);
    }
  };

  // isCreateModalOpen is local state, not derived from productFormRows — close it
  // here on the same success signal resetProductForm() already uses (rows collapsed
  // back to one empty row), so a failed/blocked save leaves the modal open.
  const wasSavingProductRef = useRef(false);
  useEffect(() => {
    if (
      wasSavingProductRef.current &&
      !isSavingProduct &&
      productFormRows.length === 1 &&
      productFormRows[0].name === '' &&
      isCreateModalOpen
    ) {
      setCreateModalOpen(false);
    }
    wasSavingProductRef.current = isSavingProduct;
  }, [isSavingProduct, productFormRows, isCreateModalOpen]);

  const [routingByRow, setRoutingByRow] = useState<Record<string, ProductRoutingChoice | null>>({});
  const wasProductEditorOpenRef = useRef(false);

  const deriveRoutingChoice = useCallback((row: ProductFormRow): ProductRoutingChoice | null => {
    if (row.kds && row.printerIds.length > 0) {
      return 'both';
    }
    if (row.kds) {
      return 'kds';
    }
    if (row.printerIds.length > 0) {
      return 'printer';
    }
    return null;
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      wasProductEditorOpenRef.current = false;
      return;
    }
    if (wasProductEditorOpenRef.current) {
      return;
    }
    wasProductEditorOpenRef.current = true;
    setRoutingByRow(() => {
      const next: Record<string, ProductRoutingChoice | null> = {};
      productFormRows.forEach((row) => {
        next[row.rowKey] = deriveRoutingChoice(row);
      });
      return next;
    });
  }, [deriveRoutingChoice, isModalOpen, productFormRows]);

  const applyRoutingChoice = useCallback((row: ProductFormRow, index: number, next: ProductRoutingChoice) => {
    setRoutingByRow((previous) => ({ ...previous, [row.rowKey]: next }));

    if (next === 'kds') {
      onChangeProductFormRowField(index, 'kds', true);
      if (row.printerIds.length > 0) {
        onChangeProductFormRowField(index, 'printerIds', []);
      }
      return;
    }

    if (next === 'printer') {
      onChangeProductFormRowField(index, 'kds', false);
      return;
    }

    onChangeProductFormRowField(index, 'kds', true);
  }, [onChangeProductFormRowField]);

  const hasValidRoutingSelection = productFormRows.every((row) => {
    const routingChoice = routingByRow[row.rowKey] ?? deriveRoutingChoice(row);
    return routingChoice === 'kds' ||
      ((routingChoice === 'printer' || routingChoice === 'both') && row.printerIds.length > 0);
  });

  const handleAreaPrintEnabledChange = useCallback((row: ProductFormRow, index: number, enabled: boolean) => {
    onChangeProductFormRowField(index, 'areaPrintEnabled', enabled);
    if (!enabled) {
      onChangeProductFormRowField(index, 'areaPrintRuleId', null);
      return;
    }
    if (areaPrintRuleOptions.length === 1) {
      onChangeProductFormRowField(index, 'areaPrintRuleId', areaPrintRuleOptions[0].id);
    }
  }, [areaPrintRuleOptions, onChangeProductFormRowField]);

  const fieldLabel = (text: string) => text;

  const renderColumnResizeHandle = (key: ProductResizableColumnKey) => (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${key} column`}
      data-testid={`product-settings-resize-${key}`}
      className={bulkClasses.columnResizeHandle}
      onMouseDown={beginColumnResize(key)}
      onClick={(event) => event.stopPropagation()}
    />
  );

  const renderSortHeader = (sortColumn: ProductSortKey, widthColumn: ProductResizableColumnKey, label: string) => (
    <th aria-sort={sortKey === sortColumn ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <div className={bulkClasses.resizableHeaderWrap}>
        <UnstyledButton
          className={bulkClasses.sortHeader}
          data-testid={`product-settings-sort-${sortColumn}`}
          onClick={() => list.toggleSort(sortColumn)}
          disabled={reorderMode}
        >
          <span>{label}</span>
          <FontAwesomeIcon
            className={bulkClasses.sortCaret}
            icon={sortKey === sortColumn ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          />
        </UnstyledButton>
        {renderColumnResizeHandle(widthColumn)}
      </div>
    </th>
  );

  const renderPlainHeader = (label: string, key: ProductResizableColumnKey) => (
    <th>
      <div className={bulkClasses.resizableHeaderWrap}>
        <span>{label}</span>
        {renderColumnResizeHandle(key)}
      </div>
    </th>
  );

  return (
    <section className={classes.section}>
      <div className={classes.toolbar}>
        <SharedButton type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="product-settings-create-open">
          צור מוצר
        </SharedButton>
        <div className={classes.formActions}>
          <Button
            type="button"
            variant="default"
            radius="md"
            data-testid="product-settings-refresh"
            onClick={onRefreshProducts}
            disabled={isLoadingProducts || isLoadingPrinters}
          >
            {isLoadingProducts || isLoadingPrinters ? 'מרענן...' : 'רענן'}
          </Button>
          <Button type="button" variant="default" radius="md" data-testid="product-settings-export" onClick={onExportProducts} disabled={isLoadingProducts}>
            ייצוא
          </Button>
          <Button type="button" variant="default" radius="md" data-testid="product-settings-import" onClick={onOpenImport}>
            ייבוא
          </Button>
          <Button
            type="button"
            variant={reorderMode ? 'filled' : 'default'}
            color="blue"
            radius="md"
            data-testid="product-settings-reorder-mode"
            aria-pressed={reorderMode}
            leftSection={<FontAwesomeIcon icon={faArrowsUpDown} />}
            onClick={handleToggleReorderMode}
          >
            {reorderMode ? 'סיום מיון' : 'מיון'}
          </Button>
        </div>
      </div>

      <TextInput
        data-testid="product-settings-search"
        placeholder="חיפוש לפי שם, מחיר או קטגוריה"
        value={list.searchQuery}
        onChange={(event) => list.setSearchQuery(event.currentTarget.value)}
        onContextMenu={(event) => event.preventDefault()}
        onSelect={(event) => {
          const input = event.currentTarget;
          const end = input.value.length;
          if (input.selectionStart !== end || input.selectionEnd !== end) {
            input.setSelectionRange(end, end);
          }
        }}
        radius="md"
        aria-label="חיפוש מוצרים"
      />

      <div className={bulkClasses.filterToolbar} dir="rtl">
        <MultiSelect
          data-testid="product-settings-filter-category"
          label="קטגוריות"
          placeholder="כל הקטגוריות"
          data={categoryOptions}
          value={list.filters.categoryIds.map((id) => String(id))}
          onChange={(values) => list.setCategoryIds(values.map((value) => Number(value)))}
          radius="md"
          searchable
          clearable
          className={bulkClasses.filterField}
        />
        <MultiSelect
          data-testid="product-settings-filter-printer"
          label="מדפסות"
          placeholder="כל המדפסות"
          data={printerOptions}
          value={list.filters.printerIds.map((id) => String(id))}
          onChange={(values) => list.setPrinterIds(values.map((value) => Number(value)))}
          radius="md"
          searchable
          clearable
          className={bulkClasses.filterField}
        />
        <div className={bulkClasses.filterSegment}>
          <span className={bulkClasses.filterLabel}>KDS</span>
          <SegmentedControl
            data-testid="product-settings-filter-kds"
            value={list.filters.kds}
            onChange={(value) => list.setKds(value as KdsFilter)}
            data={[
              { value: 'all', label: 'הכל' },
              { value: 'on', label: 'פעיל' },
              { value: 'off', label: 'כבוי' },
            ]}
          />
        </div>
        <div className={bulkClasses.filterSegment}>
          <span className={bulkClasses.filterLabel}>סטטוס</span>
          <SegmentedControl
            data-testid="product-settings-filter-status"
            value={list.filters.status}
            onChange={(value) => list.setStatus(value as StatusFilter)}
            data={[
              { value: 'all', label: 'הכל' },
              { value: 'active', label: 'פעיל' },
              { value: 'inactive', label: 'לא פעיל' },
            ]}
          />
        </div>
        <Button
          type="button"
          variant="default"
          radius="md"
          data-testid="product-settings-clear-filters"
          onClick={list.clearFilters}
          disabled={!list.hasActiveFilters}
        >
          נקה סינון
        </Button>
      </div>

      {!reorderMode && bulk.selectedCount > 0 && (
        <div className={bulkClasses.bulkBar}>
          <span className={bulkClasses.bulkCount}>{bulk.selectedCount} נבחרו</span>
          <div className={bulkClasses.bulkActions}>
            <SharedButton variant="save" size="sm" onClick={bulk.openModal} dataTestId="product-settings-bulk-edit">
              עריכה מרובה
            </SharedButton>
            <Button type="button" variant="default" radius="md" data-testid="product-settings-clear-selection" onClick={bulk.clearSelection}>
              נקה
            </Button>
          </div>
        </div>
      )}

      {!reorderMode && (
        <div className={bulkClasses.selectAllMobile}>
          <Checkbox
            data-testid="product-settings-select-all-mobile"
            label="בחר הכל"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(event) => bulk.setAll(visibleIds, event.currentTarget.checked)}
          />
        </div>
      )}

      {reorderMode && (
        <p className={bulkClasses.reorderHint} data-testid="product-settings-reorder-hint">
          גרור לפי הידית לשינוי סדר המוצרים
        </p>
      )}

      <div
        ref={tableScrollRef}
        className={`${classes.tableWrap} ${bulkClasses.productsTableWrap} ${reorderMode ? '' : bulkClasses.virtualScrollWrap}`}
      >
        {reorderMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {renderProductTable(true)}
          </DndContext>
        ) : (
          renderProductTable(false)
        )}
      </div>

      <Modal
        opened={isModalOpen}
        onClose={closeModal}
        onContextMenu={(event) => event.preventDefault()}
        title={
          <span className={bulkClasses.productEditorModalTitle} onContextMenu={(event) => event.preventDefault()}>
            {isEditMode ? 'עריכת מוצר' : 'מוצר חדש'}
            {isEditMode && editingProduct && (
              <Switch
                data-testid="product-settings-status-switch"
                aria-label={editingProduct.isActive ? 'פעיל' : 'לא פעיל'}
                onLabel="פעיל"
                offLabel="לא פעיל"
                size="lg"
                checked={editingProduct.isActive}
                color="green"
                className={`${bulkClasses.statusSwitch} ${bulkClasses.headerStatusSwitch}`}
                onChange={(event) =>
                  event.currentTarget.checked
                    ? onActivateProduct(editingProduct.id)
                    : onDeactivateProduct(editingProduct.id)
                }
                disabled={isTogglingProductStatus}
              />
            )}
          </span>
        }
        size={productEditorModalSize}
        centered={!isProductEditorTabletFullscreen}
        fullScreen={isProductEditorTabletFullscreen}
        radius="md"
        classNames={{
          root: `${navyModal.root} ${bulkClasses.productEditorModal} ${isProductEditorTabletFullscreen ? bulkClasses.createTabletFullscreenRoot : ''}`,
          content: bulkClasses.productEditorModalContent,
          body: bulkClasses.productEditorModalBody,
        }}
        closeButtonProps={{ disabled: isSavingProduct, 'aria-label': 'סגור', 'data-testid': 'product-settings-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!isSavingProduct}
        closeOnEscape={!isSavingProduct}
      >
        <form
          onSubmit={(event) => {
            if (!hasValidRoutingSelection) {
              event.preventDefault();
              return;
            }
            onSaveProduct(event);
          }}
          onContextMenu={(event) => event.preventDefault()}
          onSelect={preventProductEditorSelection}
          className={`${classes.section} ${isProductEditorTabletFullscreen ? bulkClasses.createTabletForm : ''}`}
        >
          {productFormRows.map((row, index) => (
            <div key={row.rowKey} className={bulkClasses.productFormRow}>
              {!isEditMode && index > 0 ? null : (
                (() => {
                  const routingChoice = routingByRow[row.rowKey] ?? deriveRoutingChoice(row);
                  const needsPrinter = routingChoice === 'printer' || routingChoice === 'both';

                  return (
                    <>
              {index > 0 && (
                <div className={bulkClasses.productFormRowHeader}>
                  <ActionIcon
                    aria-label="הסר מוצר"
                    data-testid={`product-settings-form-remove-${index}`}
                    variant="subtle"
                    color="red"
                    radius="md"
                    size="sm"
                    disabled={isSavingProduct}
                    onClick={() => onRemoveProductFormRow(index)}
                  >
                    <FontAwesomeIcon icon={faXmarkRemove} />
                  </ActionIcon>
                </div>
              )}
              <div className={isEditMode && !isProductEditorTabletFullscreen ? classes.formRow : bulkClasses.createFormColumn}>
                <TextInput
                  data-testid={`product-settings-form-name-${index}`}
                  label={fieldLabel('שם המוצר')}
                  placeholder="שם המוצר"
                  value={row.name}
                  onChange={(event) => onChangeProductFormRowField(index, 'name', event.currentTarget.value)}
                  minLength={1}
                  maxLength={120}
                  required
                  radius="md"
                />
                <NumberInput
                  data-testid={`product-settings-form-price-${index}`}
                  label={fieldLabel('מחיר המוצר')}
                  placeholder="מחיר"
                  value={row.price === '' ? '' : Number(row.price)}
                  onChange={(value) => onChangeProductFormRowField(index, 'price', value === '' || value === null || value === undefined ? '' : String(value))}
                  min={0}
                  decimalScale={MONEY_DECIMALS}
                  allowDecimal={MONEY_DECIMALS !== 0}
                  required
                  radius="md"
                  disabled={isSavingProduct}
                />
                <InlineCategoryCreateField
                  options={categoryOptions}
                  value={row.categoryId}
                  onChange={(categoryId) => onChangeProductFormRowField(index, 'categoryId', categoryId)}
                  onCreateCategory={onCreateCategory}
                  label={fieldLabel('קטגוריית המוצר')}
                  disabled={isSavingProduct}
                />
                <FileInput
                  label="תמונת מוצר"
                  placeholder="בחירת תמונה"
                  accept="image/jpeg,image/png,image/webp"
                  value={row.imageFile}
                  onChange={(file) => onProductFormRowImageChange(index, file)}
                  clearable
                  clearButtonProps={{ 'aria-label': 'נקה תמונה' }}
                  disabled={isSavingProduct}
                  radius="md"
                />

                <div className={bulkClasses.productRoutingField}>
                  <span className={bulkClasses.fieldLabel}>שיוך המוצר</span>
                  <SegmentedControl
                    data-testid={`product-settings-form-routing-${index}`}
                    value={routingChoice ?? ''}
                    onChange={(value) => {
                      if (!value) {
                        return;
                      }
                      applyRoutingChoice(row, index, value as ProductRoutingChoice);
                    }}
                    data={[
                      { value: 'kds', label: 'מסך מטבח' },
                      { value: 'printer', label: 'מדפסת' },
                      { value: 'both', label: 'גם וגם' },
                    ]}
                    disabled={isSavingProduct}
                    classNames={{
                      root: bulkClasses.routingSegment,
                      control: bulkClasses.routingSegmentControl,
                      label: bulkClasses.routingSegmentLabel,
                      indicator: bulkClasses.routingSegmentIndicator,
                    }}
                    fullWidth
                  />
                </div>

                {needsPrinter && (
                  <MultiSelect
                    data-testid={`product-settings-form-printers-${index}`}
                    label="שיוך מדפסות"
                    placeholder="בחר מדפסות"
                    data={printerOptionsForSelected(row.printerIds)}
                    value={row.printerIds.map((id) => String(id))}
                    onChange={(values) => onChangeProductFormRowField(index, 'printerIds', values.map((value) => Number(value)))}
                    radius="md"
                    searchable
                    clearable
                    disabled={isSavingProduct}
                    classNames={{ root: bulkClasses.printerSelectWrapper }}
                  />
                )}

                <div className={bulkClasses.areaPrintField}>
                  <span className={bulkClasses.fieldLabel}>הדפסה אזורית</span>
                  <SegmentedControl
                    data-testid={`product-settings-form-area-print-enabled-${index}`}
                    value={row.areaPrintEnabled ? 'yes' : 'no'}
                    onChange={(value) => handleAreaPrintEnabledChange(row, index, value === 'yes')}
                    data={[
                      { value: 'no', label: 'לא' },
                      { value: 'yes', label: 'כן' },
                    ]}
                    disabled={isSavingProduct}
                    classNames={{
                      root: bulkClasses.areaPrintSegment,
                      control: bulkClasses.areaPrintSegmentControl,
                      label: bulkClasses.areaPrintSegmentLabel,
                      indicator: bulkClasses.areaPrintSegmentIndicator,
                    }}
                    fullWidth
                  />

                  {row.areaPrintEnabled && (
                    <div className={bulkClasses.areaPrintRuleList}>
                      {areaPrintRuleOptions.length === 0 ? (
                        <div className={bulkClasses.areaPrintEmpty}>אין הגדרות הדפסה אזורית פעילות לבחירה.</div>
                      ) : areaPrintRuleOptions.length === 1 ? (
                        <div
                          className={`${bulkClasses.areaPrintRuleCard} ${bulkClasses.areaPrintRuleCardSelected}`}
                          data-testid={`product-settings-form-area-print-rule-${index}-${areaPrintRuleOptions[0].id}`}
                        >
                          <span className={bulkClasses.areaPrintRuleCheck}>V</span>
                          <span>{areaPrintRuleOptions[0].name}</span>
                        </div>
                      ) : (
                        areaPrintRuleOptions.map((rule) => {
                          const selected = row.areaPrintRuleId === rule.id;
                          return (
                            <button
                              key={rule.id}
                              type="button"
                              className={`${bulkClasses.areaPrintRuleCard} ${selected ? bulkClasses.areaPrintRuleCardSelected : ''}`}
                              onClick={() => onChangeProductFormRowField(index, 'areaPrintRuleId', rule.id)}
                              disabled={isSavingProduct}
                              data-testid={`product-settings-form-area-print-rule-${index}-${rule.id}`}
                            >
                              <span className={bulkClasses.areaPrintRuleCheck}>{selected ? 'V' : ''}</span>
                              <span>{rule.name}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
                    </>
                  );
                })()
              )}
            </div>
          ))}
          <div className={isEditMode && !isProductEditorTabletFullscreen ? bulkClasses.editFormFooter : bulkClasses.productFormFooter}>
            <SharedButton type="submit" variant="save" size="sm" disabled={isSavingProduct || !hasValidRoutingSelection} dataTestId="product-settings-form-submit">
              {isSavingProduct ? 'שומר...' : 'שמור'}
            </SharedButton>
          </div>
        </form>
      </Modal>

      <BulkEditProductsModal
        opened={bulk.isModalOpen}
        onClose={bulk.closeModal}
        selectedCount={bulk.selectedCount}
        categoryOptions={categoryOptions}
        printerOptions={printerOptions}
        isSubmitting={bulk.isSubmitting}
        onSubmit={(fields) => void bulk.submit(fields)}
      />

      {descriptionProduct && (
        <ProductDescriptionModal
          key={descriptionProduct.id}
          product={descriptionProduct}
          products={settingsProducts}
          employeeSession={employeeSession}
          onClose={() => setDescriptionProductId(null)}
        />
      )}
    </section>
  );
};

export const ProductSettingsSection = React.memo(ProductSettingsSectionComponent);
